import type { AclModuleChild, AclModuleRow } from './types';

/**
 * Module with its child list already parsed from the stringified JSON
 * the backend stores under `childModule`. The legacy app does the same
 * client-side parsing — the API stores child modules as a JSON string
 * blob rather than a relational table.
 */
export interface AclModuleWithChildren extends AclModuleRow {
  children: AclModuleChild[];
}

export interface PermissionFlags {
  read?: boolean;
  add?: boolean;
  edit?: boolean;
  delete?: boolean;
  view_permission?: boolean;
}

export type PermissionsMap = Record<string, PermissionFlags>;

/**
 * Parse the `childModule` JSON-string column into an array. The legacy
 * frontend sometimes saw the field as a real array (older API versions)
 * so handle both shapes.
 */
export function parseChildren(raw: unknown): AclModuleChild[] {
  if (Array.isArray(raw)) return raw as AclModuleChild[];
  if (typeof raw !== 'string' || raw.length === 0) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as AclModuleChild[]) : [];
  } catch {
    return [];
  }
}

/** Annotate every row with a parsed `children` array (defaulting to []). */
export function withParsedChildren(
  rows: AclModuleRow[],
): AclModuleWithChildren[] {
  return rows.map((m) => ({ ...m, children: parseChildren(m.childModule) }));
}

/**
 * Default permissions per module — children-bearing parents only expose
 * read + view_permission (the children carry the granular CRUD bits).
 */
export function defaultPermissionsFor(
  modules: AclModuleWithChildren[],
): PermissionsMap {
  const out: PermissionsMap = {};
  for (const m of modules) {
    const hasChild = m.children.length > 0;
    out[m.mname] = hasChild
      ? { read: false, view_permission: false }
      : {
          read: false,
          add: false,
          edit: false,
          delete: false,
          view_permission: false,
        };
    for (const c of m.children) {
      out[c.moduleName] = {
        read: false,
        add: false,
        edit: false,
        delete: false,
        view_permission: false,
      };
    }
  }
  return out;
}

/**
 * Hydrate a permissions map from an existing ACL setting's moduleOperation
 * JSON blob. Mirrors the legacy buildExpandedModulesFromPermissions logic.
 */
export function hydratePermissions(
  modules: AclModuleWithChildren[],
  moduleOperation: string | undefined,
): { permissions: PermissionsMap; expanded: Record<string, boolean> } {
  const base = defaultPermissionsFor(modules);
  const expanded: Record<string, boolean> = {};
  if (!moduleOperation) return { permissions: base, expanded };

  let parsed: Record<string, unknown> = {};
  try {
    const j = JSON.parse(moduleOperation);
    if (j && typeof j === 'object') parsed = j as Record<string, unknown>;
  } catch {
    return { permissions: base, expanded };
  }

  for (const m of modules) {
    const node = parsed[m.mname];
    if (!node || typeof node !== 'object') continue;
    const obj = node as PermissionFlags & { children?: unknown };

    // Copy parent-level flags. For children-bearing parents the legacy
    // app only stores `read` + `view_permission`, but copy whatever's
    // present defensively.
    base[m.mname] = {
      read: Boolean(obj.read),
      add: Boolean(obj.add),
      edit: Boolean(obj.edit),
      delete: Boolean(obj.delete),
      view_permission: Boolean(obj.view_permission),
    };

    if (!Array.isArray(obj.children)) continue;
    for (const childObj of obj.children) {
      if (!childObj || typeof childObj !== 'object') continue;
      const [childName, childPerms] = Object.entries(childObj)[0] ?? [];
      if (!childName) continue;
      base[childName] = {
        ...base[childName],
        ...((childPerms as PermissionFlags) ?? {}),
      };
      // Expand the parent if any of its children carry an enabled flag.
      const anyEnabled = Object.values(childPerms ?? {}).some(
        (v) => v === true,
      );
      if (anyEnabled) expanded[m.mname] = true;
    }
  }

  return { permissions: base, expanded };
}

/**
 * Build the moduleOperation JSON-string from the form state — same wire
 * shape the legacy buildAclPayload produced, but parent rows are now
 * STRUCTURAL ONLY in the UI. So we derive parent-level `read` and
 * `view_permission` from the children:
 *
 *   - parent.view_permission = OR over child.view_permission (so the
 *     sidebar group surfaces whenever any child is reachable).
 *   - parent.read = OR over child.read (rare consumer, but kept for
 *     wire compatibility with the legacy reader).
 *
 * Leaf modules (no children) keep their five flags as set by the user.
 */
export function buildModuleOperation(
  modules: AclModuleWithChildren[],
  permissions: PermissionsMap,
): string {
  const out: Record<string, unknown> = {};
  for (const m of modules) {
    const hasChild = m.children.length > 0;
    if (!hasChild) {
      out[m.mname] = { ...(permissions[m.mname] ?? {}) };
      continue;
    }
    const anyChildView = m.children.some(
      (c) => permissions[c.moduleName]?.view_permission === true,
    );
    const anyChildRead = m.children.some(
      (c) => permissions[c.moduleName]?.read === true,
    );
    out[m.mname] = {
      read: anyChildRead,
      view_permission: anyChildView,
      children: m.children.map((c) => ({
        [c.moduleName]: permissions[c.moduleName] ?? {},
      })),
    };
  }
  return JSON.stringify(out);
}
